// Fixtures para el playground local del wizard (`/dev/wizard-playground`). NO para prod.
// Portados desde `src/domains/sales/wizard/steps/PhoneSelectStep.test.tsx` para que la pantalla
// del playground se renderice sin tocar backend.
import type {
    CatalogItem,
    CatalogResponse,
    ReservedLine,
    ScopeWidePromotion,
    WizardSession,
} from '@/domains/sales/wizard/types';
import type {
    EvaluatedSalePromotion,
    ListStoreSalePromotionsResponse,
    SkuSalePromotionsResponse,
    StoreSalePromotionItem,
} from '@/domains/retail/promotions-wizard/api/wizardPromotionsContract';

export const DEV_SESSION_ID = 'WZ-DEV';

function product(over: Partial<CatalogItem> = {}): CatalogItem {
    return {
        sku: 'MOT-G55',
        brand: 'Motorola',
        model: 'Moto G55 5G',
        capacity: '256 GB',
        color: 'Negro',
        cashPriceCents: 499_900,
        stock: 3,
        category: 'PHONE',
        trackingMode: 'IMEI',
        components: [],
        financeable: true,
        eligibleFinanciers: ['SUREKEEP'],
        imageUrl: null,
        tier: 'MID',
        priorityRank: 5,
        hasPromo: false,
        mdmEnabled: false,
        mdmProvider: null,
        mdmLockMode: null,
        ...over,
    };
}

// --- Promos de alcance global (banner morado "PROMOS DE CRÉDITO") ------------
export const SCOPE_WIDE_PROMOTIONS_FIXTURE: ScopeWidePromotion[] = [
    {
        promotionId: 'SW-BONO-300',
        name: 'Bono de bienvenida PagoConFiado',
        description: 'Bono de $300 en PagoConFiado al activar el crédito',
        effect: { pcfBonusCents: 30_000 },
        segmentId: 'default',
        targetLevels: [],
    },
];

export const CATALOG_FIXTURE: CatalogResponse = {
    items: [
        product({
            sku: 'APL-IP15-128',
            brand: 'Apple',
            model: 'iPhone 15',
            capacity: '128 GB',
            cashPriceCents: 1_699_900,
            stock: 1,
            tier: 'HI',
            priorityRank: 1,
        }),
        product({
            sku: 'SAM-A56-256',
            brand: 'Samsung',
            model: 'Galaxy A56 5G',
            capacity: '256 GB',
            cashPriceCents: 949_900,
            stock: 1,
            tier: 'MID',
            priorityRank: 2,
            eligibleFinanciers: ['SUREKEEP', 'PAYJOY'],
        }),
        product({
            sku: 'MOT-G55-256',
            brand: 'Motorola',
            model: 'Moto G55 5G',
            capacity: '256 GB',
            cashPriceCents: 499_900,
            stock: 3,
            tier: 'MID',
            priorityRank: 3,
        }),
        product({
            sku: 'MOT-E40',
            brand: 'Motorola',
            model: 'Moto E40',
            capacity: '64 GB',
            cashPriceCents: 299_900,
            stock: 5,
            tier: 'LO',
            priorityRank: 4,
        }),
        product({
            sku: 'SAM-A16',
            brand: 'Samsung',
            model: 'Galaxy A16',
            capacity: '128 GB',
            cashPriceCents: 419_900,
            stock: 2,
            tier: 'LO',
            priorityRank: 5,
        }),
        product({
            sku: 'FUNDA-UNI',
            brand: 'Genérica',
            model: 'Funda universal',
            capacity: '',
            color: '',
            cashPriceCents: 19_900,
            stock: 20,
            category: 'ACCESSORY',
            trackingMode: 'QUANTITY',
            financeable: false,
            eligibleFinanciers: [],
        }),
        product({
            sku: 'KIT-G55',
            brand: 'Motorola',
            model: 'Kit Moto G55',
            capacity: '256 GB',
            cashPriceCents: 519_900,
            stock: 2,
            category: 'BUNDLE',
            components: [
                { sku: 'MOT-G55-256', qty: 1 },
                { sku: 'FUNDA-UNI', qty: 1 },
            ],
        }),
    ],
    syncedAt: new Date().toISOString(),
    scopeWidePromotions: SCOPE_WIDE_PROMOTIONS_FIXTURE,
    // "Tope de crédito $100,000.00" — se pinta en PhoneSelectStep solo si creditCapCents viene en el
    // catalog y la sesión está en SureKeep confirmado.
    creditCapCents: 10_000_000,
    // "Enganche desde 30 % — Nivel Oro" en los cards + panel derecho.
    minDownPaymentPct: 0.3,
};

// --- Promos de venta de la tienda (banner verde "PROMOS DE VENTA") -----------
// Shape de StoreSalePromotionItem con solo los fields que `saleBannerRows` + `otherLevers` usan.
// Los demás van con defaults seguros.
const EMPTY_LEVERS = {
    deviceDiscount: null,
    accessoryDiscount: null,
    gift: null,
    coupon: null,
    airtime: null,
    extendedWarranty: null,
    walletBonus: null,
} satisfies StoreSalePromotionItem['levers'];

const EMPTY_SCOPE = {
    productCategories: null,
    tiers: null,
    skus: null,
    storeIds: null,
} satisfies StoreSalePromotionItem['scope'];

// --- Promos EVALUADAS por SKU (sección DESCUENTO/BENEFICIO/REGALO del panel derecho) ---------
// Shape mínimo de `EvaluatedSalePromotion`. splitByBenefit decide group por `levers`.
function evaluatedPromo(partial: Partial<EvaluatedSalePromotion>): EvaluatedSalePromotion {
    return {
        promotionId: 'SP-?',
        version: 1,
        kind: 'SALE',
        name: 'Promo',
        customerText: '',
        levers: [],
        lines: [],
        totalDiscountCents: 0,
        giftOptions: [],
        giftChooseCount: null,
        afterSale: [],
        appliesTo: 'ANY_FINANCIER',
        stackable: false,
        anchorBound: true,
        ...partial,
    };
}

const ACCESSORY_20_EVAL = evaluatedPromo({
    promotionId: 'SP-ACC-20',
    name: 'Accesorios 20 % con tu teléfono',
    customerText: 'Con tu teléfono, 20 % de descuento en accesorios (hasta $500)',
    levers: ['ACCESSORY_DISCOUNT'],
});

// Response por SKU; para el playground, cada teléfono trae la misma promo de accesorios.
export const SKU_PROMOTIONS_FIXTURE_BY_SKU: Record<string, SkuSalePromotionsResponse> = {
    'APL-IP15-128': { sku: 'APL-IP15-128', promotions: [ACCESSORY_20_EVAL] },
    'SAM-A56-256': { sku: 'SAM-A56-256', promotions: [ACCESSORY_20_EVAL] },
    'MOT-G55-256': { sku: 'MOT-G55-256', promotions: [ACCESSORY_20_EVAL] },
    'MOT-E40': { sku: 'MOT-E40', promotions: [ACCESSORY_20_EVAL] },
    'SAM-A16': { sku: 'SAM-A16', promotions: [ACCESSORY_20_EVAL] },
    'KIT-G55': { sku: 'KIT-G55', promotions: [ACCESSORY_20_EVAL] },
};

export const STORE_SALE_PROMOTIONS_FIXTURE: ListStoreSalePromotionsResponse = {
    items: [
        {
            promotionId: 'SP-ACC-20',
            version: 1,
            name: 'Accesorios 20 % con tu teléfono',
            customerText: 'Con tu teléfono, 20 % de descuento en accesorios',
            levers: {
                ...EMPTY_LEVERS,
                accessoryDiscount: {
                    type: 'PERCENTAGE',
                    percentBps: 2000,
                    amountCents: null,
                    maxAmountCents: null,
                },
            },
            scope: { ...EMPTY_SCOPE, productCategories: ['PHONE'] },
            appliesTo: 'ANY_FINANCIER',
            stackable: false,
            anchorBound: true,
        },
    ],
};

export const SESSION_FIXTURE: WizardSession = {
    sessionId: DEV_SESSION_ID,
    state: 'ACTIVE',
    currentStep: '03',
    paymentMethodKind: null,
    phone: '9999999900',
    phoneStatus: 'NEW',
    otpVerified: true,
    otpSkipped: false,
    resumeOtpRequired: false,
    consentsAccepted: true,
    kyc: { status: 'VERIFIED', retryCount: 0 },
    accountStatus: 'OPENING',
    delivery: { cardDelivered: false, cardAlreadyOwned: false, pendingReasons: [] },
    welcome: {
        addressConfirmed: false,
        benefCaptured: false,
        annexSigned: false,
        topupApplied: false,
        level2: false,
        welcomeAccredited: false,
    },
    storeId: 'ST-DEV',
    sellerId: 'SE-DEV',
    updatedAt: new Date(0).toISOString(),
    reservedLines: [],
    // Score ya consultado: nivel GOLD → isSureKeepConfirmed(session) === true → PhoneSelectStep pinta
    // la banda "Nivel del cliente [Nivel Oro]" y permite calcular enganche con `sureKeep` en true.
    loan: { level: 'GOLD' },
};

export function makeLine(over: Partial<ReservedLine> = {}): ReservedLine {
    return {
        lineId: 'L-DEV-1',
        sku: 'MOT-G55-256',
        qty: 1,
        status: 'RESERVED',
        expiresAt: new Date(Date.now() + 20 * 60_000).toISOString(),
        basePriceCents: 499_900,
        eligibleFinanciers: ['SUREKEEP'],
        financeable: true,
        paymentType: null,
        sellItemCost: 1,
        mdmEnabled: false,
        mdmProvider: null,
        components: [],
        identifiers: [],
        product: {
            brand: 'Motorola',
            model: 'Moto G55 5G',
            capacity: '256 GB',
            color: 'Negro',
            imageUrl: null,
            tier: 'MID',
            category: 'PHONE',
            trackingMode: 'IMEI',
        },
        ...over,
    };
}
